import React, { ComponentType, useEffect, useMemo } from "react";
import posthogJs from "posthog-js";
import { PostHogProvider, setupReactErrorHandler, usePostHog } from "posthog-js/react";
import { ErrorBoundary, FallbackProps } from "react-error-boundary";

import { observeForeignDomMutations } from "../foreign-dom-mutation-observer";
import { useOrganization } from "./organization";

export function WebAnalyticsProvider({ children }: React.PropsWithChildren) {
  const { hostname, posthogApiKey } = useMemo(() => getPosthogCredentials(), []);

  const posthog = useMemo(
    () =>
      posthogJs.init(posthogApiKey, {
        api_host: hostname,
        capture_pageview: "history_change",
        capture_exceptions: true,
      }),
    [hostname, posthogApiKey]
  );

  return <PostHogProvider client={posthog}>{children}</PostHogProvider>;
}

function getPosthogCredentials() {
  // This is a public key, so it's fine to have it here
  const POSTHOG_PUBLIC_API_KEY_PROD = "phc_ktkFdO0rDVeRHplhV5uselqzvygVu7GrQ5YugwHi1ss";
  const POSTHOG_PUBLIC_API_KEY_DEV = "phc_fhoQQHLmgE4veqvMVkFXswZMsJmYFV1yIHWceEHzdvj";
  const hostname = "https://eu.i.posthog.com";

  const posthogApiKey = window.location.hostname.includes("hub.enit.io")
    ? POSTHOG_PUBLIC_API_KEY_PROD
    : POSTHOG_PUBLIC_API_KEY_DEV;

  return { hostname, posthogApiKey };
}

export function useWebAnalytics() {
  const postHog = usePostHog();
  return useMemo(
    () => ({
      sendEvent: postHog.capture.bind(postHog),
      setGroup: postHog.group.bind(postHog),
      registerSuperProperties: postHog.register.bind(postHog),
      reportError: postHog.captureException.bind(postHog),
    }),
    [postHog]
  );
}

export function makeReactRootErrorHandler() {
  return setupReactErrorHandler(posthogJs, (_event, error) => console.error(error));
}

export function reportForeignDomMutations(root: Node) {
  return observeForeignDomMutations(root, (mutation) =>
    posthogJs.capture("foreign_dom_mutation", mutation)
  );
}

export function ErrorReportingBoundary({
  FallbackComponent,
  children,
}: React.PropsWithChildren<{ FallbackComponent: ComponentType<FallbackProps> }>) {
  return <ErrorBoundary FallbackComponent={FallbackComponent}>{children}</ErrorBoundary>;
}

export function WithOrganizationWebAnalytics({ children }: React.PropsWithChildren) {
  const webAnalytics = useWebAnalytics();
  const { activeOrganization } = useOrganization();

  useEffect(() => {
    if (activeOrganization) {
      webAnalytics.setGroup("organization", activeOrganization.id, {
        name: activeOrganization.name,
        hasCo2License: activeOrganization.activeLicenses.includes("co2"),
        hasCo2FreeTrial: activeOrganization.activeLicenses.includes("co2-trial"),
        hasActionPlanLicense: activeOrganization.activeLicenses.includes("action-plan"),
        hasEdgeDevices: activeOrganization.edgeDevices.length > 0,
      });
    }
  }, [activeOrganization, webAnalytics]);

  return <>{children}</>;
}
